/**
 * Notes Sync Service
 * 
 * Periodically syncs notes from the server to ensure data consistency
 * across multiple devices. Similar to auth heartbeat pattern.
 */

import logger from '../logger';
import { fetchNotesFromCloud } from '../state/cloud/notes';
import { readSystemValue } from '../state/offline';

let notesSyncTimeout = null;
let isSyncRunning = false;
let isSyncInFlight = false;
let syncInterval = 5 * 60 * 1000; // 5 minutes default

/**
 * Start periodic notes sync
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Sync interval in milliseconds (default: 5 minutes)
 * @param {Function} options.onSyncComplete - Callback when sync completes with updated notes
 */
export const startNotesSync = ({ interval = syncInterval, onSyncComplete = null } = {}) => {
  // Prevent multiple sync instances
  if (isSyncRunning) {
    logger.debug('Notes sync already running, skipping start');
    return;
  }

  // Clear any existing sync
  if (notesSyncTimeout) {
    clearTimeout(notesSyncTimeout);
    notesSyncTimeout = null;
  }

  isSyncRunning = true;
  syncInterval = interval;
  logger.info(`Starting notes sync service (${interval / 1000 / 60} minute interval)`);

  const syncNotes = async () => {
    try {
      logger.debug('Notes sync: Checking for updates from server');
      
      // Get last sync time for delta sync
      let dateTimeSynced = null;
      try {
        const syncData = await readSystemValue({ name: 'notesLastSynced' });
        if (syncData && syncData.utcDateTime) {
          dateTimeSynced = syncData.utcDateTime;
        }
      } catch (error) {
        logger.debug('No previous sync time found, performing full sync');
      }

      // Fetch notes from cloud (with delta sync if available)
      const notes = await fetchNotesFromCloud({ dateTimeSynced });

      logger.info(
        { notesCount: Object.keys(notes || {}).length },
        'Notes sync: Sync completed successfully'
      );

      // Call callback if provided (e.g., to update state machine)
      if (onSyncComplete && typeof onSyncComplete === 'function') {
        try {
          onSyncComplete(notes);
        } catch (error) {
          logger.error({ error }, 'Error in notes sync callback');
        }
      }

      // Schedule next sync
      notesSyncTimeout = setTimeout(syncNotes, syncInterval);
    } catch (error) {
      logger.error({ error }, 'Notes sync: Error during sync');
      // On error, try to continue but log it
      // Schedule next check after interval
      notesSyncTimeout = setTimeout(syncNotes, syncInterval);
    }
  };

  // Start the first sync after a short delay (to avoid immediate sync on page load)
  // The initial sync already happens during app initialization
  notesSyncTimeout = setTimeout(syncNotes, syncInterval);
};

/**
 * Stop periodic notes sync
 */
export const stopNotesSync = () => {
  if (notesSyncTimeout) {
    clearTimeout(notesSyncTimeout);
    notesSyncTimeout = null;
  }
  isSyncRunning = false;
  isSyncInFlight = false;
  logger.info('Notes sync service stopped');
};

/**
 * Check if notes sync is currently running
 */
export const isNotesSyncRunning = () => {
  return isSyncRunning;
};

/**
 * Immediately trigger a sync outside the normal interval.
 * Safe to call at any time (e.g., on tab focus).
 */
export const syncNow = async ({ onSyncComplete = null } = {}) => {
  if (isSyncInFlight) {
    logger.debug('Notes sync: sync already in-flight, skipping');
    return;
  }
  isSyncInFlight = true;
  try {
    logger.debug('Notes sync: immediate sync triggered');

    let dateTimeSynced = null;
    try {
      const syncData = await readSystemValue({ name: 'notesLastSynced' });
      if (syncData?.utcDateTime) {
        dateTimeSynced = syncData.utcDateTime;
      }
    } catch (_) {
      // no prior sync time — full sync
    }

    const notes = await fetchNotesFromCloud({ dateTimeSynced });

    logger.info(
      { notesCount: Object.keys(notes || {}).length },
      'Notes sync: immediate sync completed'
    );

    if (onSyncComplete && typeof onSyncComplete === 'function') {
      try {
        onSyncComplete(notes);
      } catch (error) {
        logger.error({ error }, 'Error in syncNow callback');
      }
    }
  } catch (error) {
    logger.error({ error }, 'Notes sync: immediate sync failed');
  } finally {
    isSyncInFlight = false;
  }
};

